/**
 * Listens to uScope events and sets up the ability for the user to enable/activate this extension.
 */
export class ManualSyncService {
  /**
     * Constructor.
     * @param events
     * @param monitor (Object) An instance of the state monitor.
     * @param synchronizer
     * @param timer (Object) An instance of the sync timer.
     */
  constructor (events, monitor, synchronizer, timer) {
    this._monitor = monitor;
    this._synchronizer = synchronizer;
    this._timer = timer;

    // Flag to handle the case when the unpinned image is closed. The VIL closes two images; the requested image and
    // one of the pinned ones, chosen at random, which becomes the new unpinned image when it's restored. Since this
    // is a somewhat unpredictable event we save the state of all viewports before they're closed, then wait for the
    // extra closed one to be reopend and restore all viewport states.
    this._isSuspended = false;

    // Create private "_isActive" property that triggers an event on change.
    createTriggerableState('Active', events, this);

    // Create private "_isEnabled" property that triggers an event on change.
    createTriggerableState('Enabled', events, this);
  }

  /**
     * As one might expect, this method initializes the Sync instance, listening to the uScope instance for viewport
     * open, close, and selected events.
     * @param uScope (Object) The UScope instance that initializes and destroys viewports.
     */
  init (uScope) {
    // Listen for changes to uScope state.
    uScope.events.on('uscope:image:init', this._handleViewportAdded, this);
    uScope.events.on('uscope:image:close', this._handleViewportRemoved, this);
    uScope.events.on('uscope:viewport:selected', this._handleSelectedViewportChange, this);
    uScope.events.on('uscope:image:replace', this._handleImageReplace, this);

    // To handle the case when closing the sole "unpinned" image; the VIL closes two images and restores one of them
    uScope.events.on('uscope:viewport:toolbar:close-click', this._handleBeforeViewportRemoved, this);

    // Sync state with uScope and update status.
    this._refresh();
  }

  /**
     * Toggle manual sync active state.
     */
  toggle () {
    if (this._isActive) {
      this.deactivate();
    } else {
      this.activate();
    }
  }

  /**
     * Enables the service, if possible.
     */
  enable () {
    if (this._synchronizer.canSync) {
      this._isEnabled = true;
    }
  }

  /**
     * Disables the service. Deactivates if active.
     */
  disable () {
    this.deactivate();
    this._isEnabled = false;
  }

  /**
     * Activates the service if possible, causing events to propagate between active viewports.
     */
  activate () {
    if (this._isEnabled) {
      this._isActive = true;
      this._monitor.start();
      this._synchronizer.start();
      this._timer.start();
    }
  }

  /**
     * Deactivates the service. Changes will no longer propagate between viewports.
     */
  deactivate () {
    this._isActive = false;
    this._monitor.stop();
    this._synchronizer.stop();
    this._timer.stop();
  }

  /**
     * Suspends the service and saves the state of all viewports. Changes will no longer propagate between viewports.
     * When unsuspended this state will be restored.
     * Used when closing the unpinned viewport. See description @ _isSuspended declaration.
     */
  suspend () {
    this._isSuspended = true;
    this._timer.stop();
    this._monitor.stop();
    this._synchronizer.saveState();
  }

  /**
     * Restores the state of any viewports that were in view when suspended and restarts the sync service.
     * Used when closing the unpinned viewport. See description @ _isSuspended declaration.
     */
  unsuspend () {
    this._synchronizer.restoreState();
    this._monitor.start();
    this._timer.start();
    this._isSuspended = false;
  }

  /**
     * Causes the "enabled" status of the service to update if needed.
     * @private
     */
  _updateStatus () {
    if (this._synchronizer.canSync) {
      this.enable();
    } else {
      this.disable();
    }
  }

  /**
     * Sync's the monitor and synchronizer to uScope and updates the enabled state if needed.
     * @private
     */
  _refresh () {
    this._monitor.refresh();
    this._synchronizer.refresh();
    this._updateStatus();
  }

  /**
     * Replacing an image occurs when a user selects another image from the slide tray. This will swap out the unpinned
     * image for a different one. Since the new one's dimensions are not known, we treat it same as opening a new image
     * and suspend the service.
     * @private
     */
  _handleImageReplace () {
    // Image replacement also occurs when switching the unpinned image; one of the previously-pinned images
    // takes the place of the unpinned image. In this instance the user has already syncd the image's position
    // and we don't need to suspend.
    if (this._isSuspended) {
      return;
    }

    this.deactivate();
  }

  /**
     * Selected VP change event handler.
     * @private
     */
  _handleSelectedViewportChange () {
    this._refresh();

    // Switching viewports causes the monitor to stop listening for change events. Restart it.
    if (this._isActive) {
      this._monitor.start();
    }
  }

  /**
     * Viewport added event handler.
     * @private
     */
  _handleViewportAdded (viewport) {
    this._refresh();

    if (this._isSuspended) {
      // Wait for the viewport to load image data and situate itself, then unsuspend the service.
      viewport.events.once('viewport:image-opened', this.unsuspend, this);
    } else {
      // When an additional image is added, system shall unsync all opened images and automatically toggle the
      // sync button to 'off'.
      this.deactivate();
    }
  }

  /**
     * Here to handle the case when the unpinned image is closed.
     * @param viewport
     * @private
     */
  _handleBeforeViewportRemoved (viewport) {
    if (!viewport.isPinned()) {
      this.suspend();
    }
  }

  /**
     * Viewport removed event handler.
     * @private
     */
  _handleViewportRemoved () {
    if (this._isSuspended) {
      return;
    }

    // Switching viewports causes the monitor to stop listening for change events. Restart it.
    if (this._isActive) {
      this._monitor.start();
    }
  }
}

/**
 * This function creates a private getter and setter on the given context object. Changes are published on the event channel.
 * @param name (String) The name of the value to track. This value will be the basis of the event that gets published and will be prefixed with "_is" when attached to context.
 * @param events (EventChannel) The event channel to publish change events on. Format is "manual-sync:{name}-{on || off}.
 * @param context (Object) The object to attach the property to.
 */
function createTriggerableState (name, events, context) {
  // Define a backing property. Setting the initial value to "null" guarantees that initialization will cause an event
  // to be published, so we don't need to artificially do external initialization (like make button initially disabled)
  let state = null;

  // Define the property.
  Object.defineProperty(context, `_is${name}`, {
    // Simple getter.
    get () {
      return state;
    },

    // Setter with change detection and event publishing.
    set (value) {
      if (value !== state) {
        state = value;
        const event = `manual-sync:${name.toLowerCase()}-${state ? 'on' : 'off'}`;
        events.trigger(event);
      }
    }
  });
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/manual-sync/ManualSyncService.js